import { ReactNode } from 'react'
import { Navigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'

interface ProtectedRouteProps {
  children: ReactNode
  requireReviewer?: boolean
}

/**
 * ProtectedRoute component that checks authentication before rendering children.
 * Redirects to /login if user is not authenticated.
 * 
 * Can be bypassed in test/development mode by setting:
 * - VITE_ALLOW_UNAUTHENTICATED_ACCESS=true (environment variable)
 * - Or automatically in development mode (import.meta.env.DEV)
 */
export default function ProtectedRoute({ children, requireReviewer = false }: ProtectedRouteProps) {
  const { user, isReviewer, isLoading } = useAuth()

  // Check if unauthenticated access is allowed (for testing/development)
  const allowUnauthenticated = 
    import.meta.env.VITE_ALLOW_UNAUTHENTICATED_ACCESS === 'true' ||
    import.meta.env.DEV // Automatically allow in development mode

  // Show loading state while checking authentication
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    )
  }

  // If unauthenticated access is allowed, render content even without user
  if (allowUnauthenticated) {
    return <>{children}</>
  }

  // Redirect to login if not authenticated (production mode)
  if (!user) {
    return <Navigate to="/login" replace />
  }

  // Check reviewer requirement
  if (requireReviewer && !isReviewer) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">Reviewer access required</p>
        </div>
      </div>
    )
  }

  // User is authenticated (and reviewer if required), render the protected content
  return <>{children}</>
}

